import './api/bootstrapApiUrl.js'
import React, { useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom'
import * as Sentry from "@sentry/react"
import './index.css'
import App from './App.jsx'

const sentryDsn = import.meta.env.VITE_SENTRY_DSN

const apiBaseUrl = globalThis.__API_BASE_URL__
// Billing chrome (nav badge, Settings plan card) stays off until Pro features ship.
// /pricing is always available by URL. Set VITE_BILLING_ENABLED=true to show chrome.
globalThis.__BILLING_ENABLED__ =
  String(import.meta.env.VITE_BILLING_ENABLED || "").toLowerCase() === "true"

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_APP_VERSION || undefined,

    integrations: [
      // Performance monitoring + automatic route transactions for react-router v7.
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      // Session Replay: records a video-like reproduction of what the user did.
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      // Send console.warn / console.error to Sentry Logs (Issues still come from crashes + API 5xx).
      Sentry.consoleLoggingIntegration({
        levels: import.meta.env.PROD ? ["warn", "error"] : ["log", "info", "warn", "error"],
      }),
    ],

    // Sentry Logs product (separate from Issues). Needed on SDK < 10.71.
    enableLogs: true,

    // --- Performance tracing ---
    // Sample a fraction of transactions. Lower this if you exceed your quota.
    tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
    // Connect frontend traces to the backend by sending trace headers to the API.
    tracePropagationTargets: [apiBaseUrl, /^\/api\//],

    // --- Session Replay sampling ---
    // Don't record every session (privacy + quota); always record sessions with errors.
    replaysSessionSampleRate: import.meta.env.PROD ? 0.05 : 0,
    replaysOnErrorSampleRate: 1.0,

    // Don't send personally identifiable info (IP, request bodies) by default.
    sendDefaultPii: false,
  })
}

createRoot(document.getElementById('root')).render(
  <Sentry.ErrorBoundary
    fallback={({ resetError }) => (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-8 text-center">
        <h1 className="text-2xl font-semibold">Something went wrong</h1>
        <p className="text-gray-500">
          The error has been reported. Try reloading the page.
        </p>
        <button
          onClick={resetError}
          className="px-4 py-2 rounded-md bg-indigo-600 text-white"
        >
          Try again
        </button>
      </div>
    )}
  >
    <App />
  </Sentry.ErrorBoundary>
)
